/**
 * useRealtimeTasks Hook
 *
 * Phase 5: Real-time task updates integration
 * Based on realtime-communication-skill guidelines
 *
 * This hook connects to the Socket.io server and provides
 * real-time task updates for the Dispatch Console
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { message, notification } from 'antd';
import { socketService } from '../services/socket';
import { formatApptDateTime } from '../utils/apptFormat';

interface TaskUpdateData {
  taskId: number;
  task_id?: string;
  status: string;
  /** task_resumed 专有：恢复到哪个状态。这条推送不带 status。 */
  resumedToStatus?: string;
  container_number?: string;
  driver_name?: string;
  action?: string;
  updatedFields?: string[];
  timestamp?: Date;
  /**
   * 监控推的状态变动会带上原因，目前只有封闭区用：
   * closed_area / closed_area_cleared / closed_area_cleared_rebook
   */
  reason?: string;
  /** 封闭区把任务从哪个状态拉回来的（unassigned / assigned / picking） */
  recalledFrom?: string;
  /** closed_area_cleared_rebook 专有：作废的那张票的时间和号 */
  appointmentTime?: string;
  appointmentNumber?: string;
  /** closed_area_cleared_rebook 专有：这趟原来派的司机被摘掉了 */
  driverDropped?: boolean;
  source?: string;
}

interface BatchUpdateData {
  taskIds: number[];
  status?: string;
  driverId?: number;
  driverName?: string;
  count: number;
  action: string;
}

interface OrderUpdateData {
  orderId: string;
  taskIds: number[];
  updatedFields?: string[];
  updatedAt?: Date;
}

interface UseRealtimeTasksOptions {
  enabled?: boolean;
  onTaskUpdate?: (data: TaskUpdateData) => void;
  onTaskCreated?: (data: TaskUpdateData) => void;
  onTaskCompleted?: (data: TaskUpdateData) => void;
  onTaskCancelled?: (data: TaskUpdateData) => void;
  onTaskPaused?: (data: TaskUpdateData) => void;
  onTaskResumed?: (data: TaskUpdateData) => void;
  onTaskUnlocked?: (data: TaskUpdateData) => void;
  onDriverAssigned?: (data: { taskId: number; driverId: number; driverName: string }) => void;
  onBatchUpdate?: (data: BatchUpdateData) => void;
  onOrderUpdate?: (data: OrderUpdateData) => void;
}

interface UseRealtimeTasksReturn {
  isConnected: boolean;
  connect: (token: string) => Promise<void>;
  disconnect: () => void;
  lastUpdate: Date | null;
}

export const useRealtimeTasks = (options: UseRealtimeTasksOptions = {}): UseRealtimeTasksReturn => {
  const {
    enabled = true,
    onTaskUpdate,
    onTaskCreated,
    onTaskCompleted,
    onTaskCancelled,
    onTaskPaused,
    onTaskResumed,
    onTaskUnlocked,
    onDriverAssigned,
    onBatchUpdate,
    onOrderUpdate
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const lastUpdateRef = useRef<Date | null>(null);
  const listenersSetup = useRef(false);

  // Handle task_updated event
  //
  // 没带 status 的那种 task_updated 必须在这里挡掉。
  //
  // 下游拿到 data.status 就直接喂给 mapBackendStatusToFrontend()，而那个函数
  // 认不出来的值一律回落成 'released' —— undefined 也算认不出来。于是一条只
  // 想说「ETA 改了」的推送，会把别人看板上那一行的状态改写成「已放行」，而这
  // 行任务可能正在送货。后端确实有这种只带 action 不带 status 的 emit
  // (updateEtaResume / confirmPairing)。
  //
  // 下游只用 status 这一个字段，所以丢掉这类推送不会少任何东西。
  const handleTaskUpdated = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task updated:', data);
    lastUpdateRef.current = new Date();
    if (!data?.status) return;

    // 封闭区必须出声，不能只是行悄悄换个分组。
    //
    // 码头把柜子关起来时，任务可能已经派出去、司机已经在路上了 —— 看板重排
    // 一下没人会注意到，而这正是要人立刻改主意的一条。解除时同样要说，
    // 否则调度不知道柜子什么时候重新能提。
    // 只在这里做：两条渲染管道都走同一个 hook。
    if (data.reason === 'closed_area') {
      const who = data.recalledFrom === 'picking' ? ' (the driver may already be on the way)' : '';
      message.warning(
        `${data.container_number || `Task ${data.task_id || data.taskId}`} — terminal moved it into a closed area. ` +
        `It can't be picked up; the task is back to On Hold.${who}`,
        10
      );
    } else if (data.reason === 'closed_area_cleared') {
      message.success(
        `${data.container_number || `Task ${data.task_id || data.taskId}`} released from the closed area — ` +
        `task back to ${data.status}.`,
        8
      );
    } else if (data.reason === 'closed_area_cleared_rebook') {
      // 柜子放出来了，但原来那张票已经来不及 —— 这一条要人动手（去重约），
      // 所以用右上角的通知而不是一闪而过的 message：它会停在那儿等人读。
      const box = data.container_number || `Task ${data.task_id || data.taskId}`;
      const when = data.appointmentTime ? formatApptDateTime(data.appointmentTime) : '';
      notification.warning({
        message: `${box} released`,
        description:
          `The old appointment${when ? ` (${when})` : ''} no longer leaves enough time. `
          + 'Marked Rebook on the Appt and the task is back to Released. '
          + (data.driverDropped ? 'The driver was taken off this run — reassign after rebooking.' : 'Book a new slot.'),
        placement: 'topRight',
        duration: 12,
      });
    }

    onTaskUpdate?.(data);
  }, [onTaskUpdate]);

  // Handle task_created event
  const handleTaskCreated = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task created:', data);
    lastUpdateRef.current = new Date();
    onTaskCreated?.(data);
  }, [onTaskCreated]);

  // Handle task_completed event
  const handleTaskCompleted = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task completed:', data);
    lastUpdateRef.current = new Date();
    onTaskCompleted?.(data);
  }, [onTaskCompleted]);

  // Handle task_cancelled event
  const handleTaskCancelled = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task cancelled:', data);
    lastUpdateRef.current = new Date();
    // Also trigger onTaskUpdate for status change
    onTaskUpdate?.({ ...data, status: 'cancelled' });
    onTaskCancelled?.(data);
  }, [onTaskUpdate, onTaskCancelled]);

  // Handle task_paused event
  const handleTaskPaused = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task paused:', data);
    lastUpdateRef.current = new Date();
    // Also trigger onTaskUpdate for status change
    onTaskUpdate?.({ ...data, status: 'paused' });
    onTaskPaused?.(data);
  }, [onTaskUpdate, onTaskPaused]);

  // Handle task_resumed event
  //
  // 恢复后的状态在 resumedToStatus 里，不在 status 里 —— 后端这条推送根本
  // 没有 status 字段。照原样往下传的话，下游会把这一行改写成「已放行」
  // （见 handleTaskUpdated 的注释）。任务恢复到的可能是 delivering、
  // loading……唯独不会是 released。
  const handleTaskResumed = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task resumed:', data);
    lastUpdateRef.current = new Date();
    const resumedTo = data.resumedToStatus || data.status;
    if (resumedTo) onTaskUpdate?.({ ...data, status: resumedTo });
    onTaskResumed?.(data);
  }, [onTaskUpdate, onTaskResumed]);

  // Handle task_unlocked event
  const handleTaskUnlocked = useCallback((data: TaskUpdateData) => {
    console.log('[Realtime] Task unlocked:', data);
    lastUpdateRef.current = new Date();
    onTaskUpdate?.(data);
    onTaskUnlocked?.(data);
  }, [onTaskUpdate, onTaskUnlocked]);

  // Handle driver_assigned event
  const handleDriverAssigned = useCallback((data: { taskId: number; driverId: number; driverName: string; assignedAt: Date }) => {
    console.log('[Realtime] Driver assigned:', data);
    lastUpdateRef.current = new Date();
    onDriverAssigned?.(data);
  }, [onDriverAssigned]);

  // Handle tasks_batch_updated event
  const handleBatchUpdated = useCallback((data: BatchUpdateData) => {
    console.log('[Realtime] Batch updated:', data);
    lastUpdateRef.current = new Date();
    onBatchUpdate?.(data);
  }, [onBatchUpdate]);

  // Handle order_updated event (for syncing Task page with Order changes)
  const handleOrderUpdated = useCallback((data: OrderUpdateData) => {
    console.log('[Realtime] Order updated:', data);
    lastUpdateRef.current = new Date();
    onOrderUpdate?.(data);
  }, [onOrderUpdate]);

  // Setup event listeners
  const setupListeners = useCallback(() => {
    // Clean up old listeners before re-registering (handles reconnect scenario)
    if (listenersSetup.current) {
      socketService.off('task_updated', handleTaskUpdated);
      socketService.off('task_created', handleTaskCreated);
      socketService.off('task_completed', handleTaskCompleted);
      socketService.off('task_cancelled', handleTaskCancelled);
      socketService.off('task_paused', handleTaskPaused);
      socketService.off('task_resumed', handleTaskResumed);
      socketService.off('task_unlocked', handleTaskUnlocked);
      socketService.off('driver_assigned', handleDriverAssigned);
      socketService.off('tasks_batch_updated', handleBatchUpdated);
      socketService.off('order_updated', handleOrderUpdated);
    }

    socketService.on('task_updated', handleTaskUpdated);
    socketService.on('task_created', handleTaskCreated);
    socketService.on('task_completed', handleTaskCompleted);
    socketService.on('task_cancelled', handleTaskCancelled);
    socketService.on('task_paused', handleTaskPaused);
    socketService.on('task_resumed', handleTaskResumed);
    socketService.on('task_unlocked', handleTaskUnlocked);
    socketService.on('driver_assigned', handleDriverAssigned);
    socketService.on('tasks_batch_updated', handleBatchUpdated);
    socketService.on('order_updated', handleOrderUpdated);

    listenersSetup.current = true;
    console.log('[Realtime] Event listeners setup complete');
  }, [handleTaskUpdated, handleTaskCreated, handleTaskCompleted, handleTaskCancelled, handleTaskPaused, handleTaskResumed, handleTaskUnlocked, handleDriverAssigned, handleBatchUpdated, handleOrderUpdated]);

  // Cleanup event listeners
  const cleanupListeners = useCallback(() => {
    if (!listenersSetup.current) return;

    socketService.off('task_updated', handleTaskUpdated);
    socketService.off('task_created', handleTaskCreated);
    socketService.off('task_completed', handleTaskCompleted);
    socketService.off('task_cancelled', handleTaskCancelled);
    socketService.off('task_paused', handleTaskPaused);
    socketService.off('task_resumed', handleTaskResumed);
    socketService.off('task_unlocked', handleTaskUnlocked);
    socketService.off('driver_assigned', handleDriverAssigned);
    socketService.off('tasks_batch_updated', handleBatchUpdated);
    socketService.off('order_updated', handleOrderUpdated);

    listenersSetup.current = false;
    console.log('[Realtime] Event listeners cleaned up');
  }, [handleTaskUpdated, handleTaskCreated, handleTaskCompleted, handleTaskCancelled, handleTaskPaused, handleTaskResumed, handleTaskUnlocked, handleDriverAssigned, handleBatchUpdated, handleOrderUpdated]);

  // Connect to socket server
  const connect = useCallback(async (token: string) => {
    try {
      await socketService.connect(token);
      setIsConnected(true);
      setupListeners();
      console.log('[Realtime] Connected to socket server');
    } catch (error) {
      console.error('[Realtime] Failed to connect:', error);
      setIsConnected(false);
    }
  }, [setupListeners]);

  // Disconnect from socket server
  const disconnect = useCallback(() => {
    cleanupListeners();
    socketService.disconnect();
    setIsConnected(false);
    console.log('[Realtime] Disconnected from socket server');
  }, [cleanupListeners]);

  // Auto-connect when enabled and token is available
  useEffect(() => {
    if (!enabled) return;

    const token = localStorage.getItem('token');
    if (token && !socketService.isSocketConnected()) {
      connect(token);
    } else if (socketService.isSocketConnected()) {
      setIsConnected(true);
      setupListeners();
    }

    // Update connection status periodically — only when value actually changes
    const interval = setInterval(() => {
      const connected = socketService.isSocketConnected();
      setIsConnected(prev => prev === connected ? prev : connected);
    }, 5000);

    return () => {
      clearInterval(interval);
      cleanupListeners();
    };
  }, [enabled, connect, setupListeners, cleanupListeners]);

  return {
    isConnected,
    connect,
    disconnect,
    lastUpdate: lastUpdateRef.current
  };
};

